import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { fmtDag, mag, ROL_LABEL, VERSIE, VERSIE_DATUM, WIJZIGINGEN, type Recht } from '@ep/core';
import { api } from '../lib/api';
import { useIk, type Data } from '../lib/data';
import { Icoon, type IcoonNaam } from './Icoon';
import { useThema, type Thema } from '../lib/thema';
import { toast } from './toast';
import { Modal } from './ui';
import { TimerKnop } from './Uren';
import { WijzigingenLijst } from './Wijzigingen';
import { OrganisatieKiezer } from './OrganisatieKiezer';
import { MeldingenBel } from './Meldingen';

/** SPEC §14.1: onderdelen van de zijbalk. */
interface NavItem {
  naar: string;
  label: string;
  icoon: IcoonNaam;
  recht?: Recht;
  eid?: boolean;
  exact?: boolean;
  actiefBij?: string[];
}
interface NavGroep {
  groep: string | null;
  module?: 'UREN' | 'AGILE';
  items: NavItem[];
}

const NAV: NavGroep[] = [
  { groep: null, items: [{ naar: '/', label: 'Start', icoon: 'house', exact: true }] },
  {
    groep: 'Plannen',
    items: [
      { naar: '/projecten', label: 'Projecten', icoon: 'folder-kanban', recht: 'projecten.lezen' },
      { naar: '/tijdlijn', label: 'Tijdlijn', icoon: 'chart-gantt', recht: 'projecten.lezen' },
      { naar: '/taken', label: 'Taken', icoon: 'list-checks', recht: 'taken.lezen' },
      { naar: '/weekrooster', label: 'Weekrooster', icoon: 'calendar-range', recht: 'rooster' },
    ],
  },
  {
    groep: 'Mijn werk',
    items: [
      { naar: '/mijn-taken', label: 'Mijn taken', icoon: 'clipboard-list', eid: true },
      { naar: '/mijn-uren', label: 'Mijn uren', icoon: 'clock', eid: true },
    ],
  },
  {
    groep: 'Team',
    items: [
      { naar: '/verlof', label: 'Verlof en afwezigheid', icoon: 'tree-palm', recht: 'verlof' },
      { naar: '/bezetting', label: 'Bezetting', icoon: 'chart-column', recht: 'rooster' },
      { naar: '/expertise', label: 'Expertise-matrix', icoon: 'layout-grid', recht: 'rooster' },
    ],
  },
  {
    groep: 'Uren',
    module: 'UREN',
    items: [
      { naar: '/uren/rapport', label: 'Urenrapport', icoon: 'file-chart-column', recht: 'uren.alle' },
      { naar: '/uren/goedkeuren', label: 'Uren goedkeuren', icoon: 'badge-check', recht: 'uren.goedkeuren' },
    ],
  },
  {
    groep: 'Beheer',
    items: [
      { naar: '/beheer', label: 'Engineers', icoon: 'hard-hat', recht: 'beheer', exact: true },
      { naar: '/templates', label: 'Templates', icoon: 'layout-template', recht: 'templates' },
      { naar: '/beheer/gebruikers', label: 'Gebruikers', icoon: 'users', recht: 'beheer' },
      { naar: '/beheer/instellingen', label: 'Instellingen', icoon: 'settings', recht: 'beheer' },
    ],
  },
];

const NAV_ENGINEER: NavGroep[] = [
  {
    groep: null,
    items: [
      { naar: '/', label: 'Mijn taken', icoon: 'clipboard-list', exact: true, actiefBij: ['/mijn-taken'] },
      { naar: '/mijn-uren', label: 'Mijn uren', icoon: 'clock' },
      { naar: '/mijn-agenda', label: 'Mijn agenda', icoon: 'calendar-plus' },
    ],
  },
];

function useNav() {
  const s = useIk().data!;
  const lic = s.licentie;
  const geblokkeerd = lic?.toegang === 'GEBLOKKEERD';
  const mods = lic?.modules ?? ['AGILE', 'UREN'];
  if (geblokkeerd) return [] as NavGroep[];
  const bron = s.rol === 'ENGINEER' ? NAV_ENGINEER : NAV;
  return bron
    .filter((g) => !g.module || mods.includes(g.module))
    .map((g) => ({
      ...g,
      items: g.items.filter((i) => (!i.recht || mag(s.rol, i.recht)) && (!i.eid || !!s.eid) && (i.naar !== '/mijn-uren' || mods.includes('UREN'))),
    }))
    .filter((g) => g.items.length);
}

/** Gedeelde /api/data-cache (niet voor engineers). */
function useDataStil(aan: boolean) {
  return useQuery({ queryKey: ['data'], queryFn: () => api.get<Data>('/api/data'), enabled: aan, staleTime: 30000 });
}

/** SPEC §14.1: kruimelpad uit het adres. */
function kruimels(pad: string, d: Data | undefined, engineer: boolean): { label: string; naar?: string }[] {
  const delen = pad.split('/').filter(Boolean);
  const start = { label: engineer ? 'Mijn taken' : 'Start', naar: '/' };
  if (!delen.length) return [{ label: start.label }];
  const uit: { label: string; naar?: string }[] = [start];
  const vast: Record<string, string> = {
    projecten: 'Projecten', tijdlijn: 'Tijdlijn', taken: 'Taken', weekrooster: 'Weekrooster', verlof: 'Verlof en afwezigheid', bezetting: 'Bezetting',
    expertise: 'Expertise-matrix', templates: 'Templates', beheer: 'Engineers', 'mijn-taken': 'Mijn taken', 'mijn-uren': 'Mijn uren', 'mijn-agenda': 'Mijn agenda',
  };
  if (delen[0] === 'projecten') {
    uit.push({ label: 'Projecten', naar: '/projecten' });
    const p = delen[1] ? d?.projecten.find((x) => x.id === delen[1]) : undefined;
    if (delen[1]) uit.push({ label: p ? `${p.nummer} ${p.naam}` : 'Project', naar: `/projecten/${delen[1]}` });
    const sub: Record<string, string> = { werkpakketten: 'Werkzaamheden', planning: 'Projectplanning', rapport: 'Rapport', agile: 'Agile' };
    if (delen[2] === 'agile' && delen[3]) uit.push({ label: `${d?.werkpakketten.find((w) => w.id === delen[3])?.naam ?? 'Werkpakket'} (agile)` });
    else if (delen[2]) uit.push({ label: sub[delen[2]] ?? delen[2] });
  } else if (delen[0] === 'taken' && delen[1]) {
    uit.push({ label: 'Taken', naar: '/taken' });
    uit.push({ label: delen[1] === 'nieuw' ? 'Nieuwe taak' : d?.taken.find((t) => t.id === delen[1])?.titel ?? 'Taak' });
  } else if (delen[0] === 'beheer' && delen[1]) {
    uit.push({ label: delen[1] === 'gebruikers' ? 'Gebruikers' : 'Instellingen' });
  } else if (delen[0] === 'uren') {
    uit.push({ label: delen[1] === 'goedkeuren' ? 'Uren goedkeuren' : 'Urenrapport' });
  } else uit.push({ label: vast[delen[0]] ?? delen[0] });
  uit[uit.length - 1] = { label: uit[uit.length - 1].label };
  return uit;
}

const leesIngeklapt = () => {
  try {
    return localStorage.getItem('ep-zijbalk') === 'klein';
  } catch {
    return false;
  }
};

/** SPEC §14.1: zijbalk, bovenbalk, zoeken en meldingen rond elke pagina. */
export function Shell({ children }: { children: ReactNode }) {
  const s = useIk().data!;
  const qc = useQueryClient();
  const nav = useNavigate();
  const loc = useLocation();
  const groepen = useNav();
  const engineer = s.rol === 'ENGINEER';
  const d = useDataStil(!engineer && s.licentie?.toegang !== 'GEBLOKKEERD').data;
  const [klein, setKlein] = useState(leesIngeklapt);
  const [mobielOpen, setMobielOpen] = useState(false);
  const [zoek, setZoek] = useState(false);
  const [menu, setMenu] = useState(false);
  const [over, setOver] = useState(false);
  // SPEC §17.2: keuzescherm na het inloggen (/?kies=1) of via het gebruikersmenu
  const [kiezer, setKiezer] = useState<'inloggen' | 'wissel' | null>(() => (new URLSearchParams(window.location.search).get('kies') === '1' ? 'inloggen' : null));
  const sluitKiezer = () => {
    setKiezer(null);
    if (new URLSearchParams(window.location.search).has('kies')) nav(loc.pathname, { replace: true });
  };
  const [thema, zetThema] = useThema();
  const menuRef = useRef<HTMLDivElement>(null);
  const hs = s.organisatie.huisstijl;
  const lic = s.licentie;
  const urenAan = lic?.toegang !== 'GEBLOKKEERD' && (lic?.modules.includes('UREN') ?? true);

  useEffect(() => {
    setMobielOpen(false);
    setMenu(false);
  }, [loc.pathname]);
  useEffect(() => {
    try {
      localStorage.setItem('ep-zijbalk', klein ? 'klein' : 'groot');
    } catch {
      /* geen opslag: niet erg */
    }
  }, [klein]);
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k' && !engineer) {
        e.preventDefault();
        setZoek((z) => !z);
      }
    };
    const klik = (e: MouseEvent) => menuRef.current && !menuRef.current.contains(e.target as Node) && setMenu(false);
    window.addEventListener('keydown', k);
    document.addEventListener('mousedown', klik);
    return () => {
      window.removeEventListener('keydown', k);
      document.removeEventListener('mousedown', klik);
    };
  }, [engineer]);

  const uitloggen = async () => {
    await api.post('/auth/uitloggen');
    qc.clear();
    window.location.href = '/';
  };
  const ververs = async () => {
    await qc.invalidateQueries();
    toast('Gegevens vernieuwd', 'succes');
  };
  const actief = (i: NavItem) =>
    i.exact ? loc.pathname === i.naar || (i.actiefBij ?? []).some((p) => loc.pathname.startsWith(p)) : loc.pathname === i.naar || loc.pathname.startsWith(i.naar + '/');
  const achter = (loc.state as { achtergrond?: { pathname: string } } | null)?.achtergrond;
  const pad = kruimels(/^\/taken\/[^/]+$/.test(loc.pathname) && achter ? achter.pathname : loc.pathname, d, engineer);
  const initialen = s.naam.split(/\s+/).filter(Boolean).map((x) => x[0]).slice(0, 2).join('').toUpperCase();
  const mac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

  return (
    <div className={`shell ${klein ? 'klein' : ''} ${mobielOpen ? 'mobiel-open' : ''} ${engineer ? 'is-engineer' : ''}`}>
      <aside className="zijbalk geen-print">
        <div className="zb-merk">
          {hs?.logoVersie ? (
            <Link to="/" className="zb-org-logo" title={s.organisatie.naam}>
              <span><img src={`/api/huisstijl/logo?v=${hs.logoVersie}`} alt={s.organisatie.naam} /></span>
              {!klein && <small>powered by <b>bsmart<i>r</i></b></small>}
            </Link>
          ) : (
            <Link to="/" title="bsmartr" className="zb-bsmartr">{klein ? <img src="/favicon.svg" alt="bsmartr" className="zb-icoon-logo" /> : <img src="/logo-wit.svg" alt="bsmartr" />}</Link>
          )}
          {!klein && <div className="zb-org">{s.organisatie.naam}</div>}
        </div>
        <nav className="zb-nav">
          {groepen.map((g, gi) => (
            <div key={gi} className="zb-groep">
              {g.groep && !klein && <div className="zb-groep-naam">{g.groep}</div>}
              {g.groep && klein && <div className="zb-scheiding" />}
              {g.items.map((i) => (
                <NavLink key={i.naar} to={i.naar} end={i.exact} className={() => `zb-item ${actief(i) ? 'actief' : ''}`} title={klein ? i.label : undefined}>
                  <Icoon naam={i.icoon} maat={18} />
                  {!klein && <span>{i.label}</span>}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <button className="zb-inklap" onClick={() => setKlein(!klein)} title={klein ? 'Uitklappen' : 'Inklappen'}>
          <Icoon naam={klein ? 'panel-left-open' : 'panel-left-close'} maat={18} />
          {!klein && <span>Inklappen</span>}
        </button>
      </aside>
      {mobielOpen && <div className="zb-scherm geen-print" onClick={() => setMobielOpen(false)} />}

      <div className="shell-hoofd">
        {s.imp && (
          <div className="melding-balk meekijken geen-print">
            👁 Je kijkt mee als beheerder van <b>{s.organisatie.naam}</b> (platformbeheer: {s.imp}). Wijzigingen zijn echt.
            <button className="knop klein" onClick={uitloggen}>Stoppen met meekijken</button>
          </div>
        )}
        {lic?.toegang === 'MELDING' && lic.melding && (
          <div className="melding-balk licentie geen-print">
            <Icoon naam="triangle-alert" maat={16} /> <span className="groei">{lic.melding}</span>
            {mag(s.rol, 'beheer') && <Link className="knop klein" to="/beheer/instellingen?tab=abonnement">Abonnement</Link>}
          </div>
        )}
        <header className="bovenbalk geen-print">
          {!engineer && (
            <button className="bb-knop bb-hamburger" onClick={() => setMobielOpen(true)} title="Menu">
              <Icoon naam="menu" />
            </button>
          )}
          <nav className="kruimels" aria-label="Kruimelpad">
            {pad.map((k, i) => (
              <span key={i} className="kruimel">
                {i > 0 && <Icoon naam="chevron-right" maat={14} className="kruimel-pijl" />}
                {k.naar ? <Link to={k.naar}>{k.label}</Link> : <b>{k.label}</b>}
              </span>
            ))}
          </nav>
          <div className="groei" />
          {!engineer && lic?.toegang !== 'GEBLOKKEERD' && (
            <button className="bb-zoek" onClick={() => setZoek(true)}>
              <Icoon naam="search" maat={16} />
              <span>Zoeken…</span>
              <kbd>{mac ? '⌘' : 'Ctrl'} K</kbd>
            </button>
          )}
          {urenAan && <TimerKnop />}
          {s.uid && lic?.toegang !== 'GEBLOKKEERD' && <MeldingenBel />}
          <div className="bb-menu" ref={menuRef}>
            <button className="bb-avatar" onClick={() => setMenu(!menu)} title={`${s.naam} · ${s.email}`}>
              {initialen || <Icoon naam="user" />}
            </button>
            {menu && (
              <div className="menu-lijst">
                <div style={{ padding: '8px 10px' }}>
                  <div className="vet">{s.naam}</div>
                  <div className="klein mute">{s.email}</div>
                  <div className="klein mute">{s.rol ? ROL_LABEL[s.rol] : 'gast'} · {s.organisatie.naam}</div>
                </div>
                <div className="thema-keuze" role="group" aria-label="Weergave">
                  {([['licht', 'sun', 'Licht'], ['donker', 'moon', 'Donker'], ['systeem', 'laptop', 'Systeem']] as [Thema, IcoonNaam, string][]).map(([t, ic, l]) => (
                    <button key={t} className={thema === t ? 'aan' : ''} onClick={() => zetThema(t)} title={t === 'systeem' ? 'Volgt de instelling van je computer' : l}>
                      <Icoon naam={ic} maat={16} />
                      {l}
                    </button>
                  ))}
                </div>
                {(s.organisaties ?? 1) > 1 && (
                  <button onClick={() => { setMenu(false); setKiezer('wissel'); }}><Icoon naam="arrow-left-right" maat={16} /> Wissel organisatie <span className="klein mute">({s.organisaties})</span></button>
                )}
                <button onClick={() => nav('/mijn-agenda')}><Icoon naam="calendar-plus" maat={16} /> Mijn agenda koppelen</button>
                <button onClick={() => { setMenu(false); setOver(true); }}><Icoon naam="info" maat={16} /> Over bsmartr <span className="klein mute">v{VERSIE}</span></button>
                <button onClick={() => { setMenu(false); void ververs(); }} title="Gegevens worden ook vanzelf ververst: na elke wijziging en als je terugkomt in dit tabblad"><Icoon naam="refresh-cw" maat={16} /> Gegevens vernieuwen</button>
                <button onClick={uitloggen}><Icoon naam="log-out" maat={16} /> Uitloggen</button>
              </div>
            )}
          </div>
        </header>
        <main className="shell-inhoud">{children}</main>
        {engineer && lic?.toegang !== 'GEBLOKKEERD' && (
          <nav className="tabbalk geen-print">
            <NavLink to="/" end className={() => (loc.pathname === '/' || loc.pathname.startsWith('/mijn-taken') ? 'actief' : '')}><Icoon naam="clipboard-list" /><span>Taken</span></NavLink>
            {urenAan && <NavLink to="/mijn-uren"><Icoon naam="clock" /><span>Uren</span></NavLink>}
            <NavLink to="/mijn-agenda"><Icoon naam="calendar-days" /><span>Agenda</span></NavLink>
          </nav>
        )}
      </div>
      {zoek && d && <ZoekPalet d={d} onSluit={() => setZoek(false)} />}
      {over && <OverBsmartr onSluit={() => setOver(false)} />}
      {kiezer && (s.organisaties ?? 1) > 1 && <OrganisatieKiezer naInloggen={kiezer === 'inloggen'} onSluit={sluitKiezer} />}
    </div>
  );
}

interface Resultaat {
  sleutel: string;
  soort: string;
  titel: string;
  sub?: string;
  icoon: IcoonNaam;
  naar: string;
}

/** SPEC §19: tekstwaarden van eigen velden doorzoekbaar. */
const tekstVelden = (v?: Record<string, unknown>) => Object.values(v ?? {}).filter((x): x is string => typeof x === 'string');

/** SPEC §14.1: zoeken met ⌘K. */
function ZoekPalet({ d, onSluit }: { d: Data; onSluit: () => void }) {
  const s = useIk().data!;
  const nav = useNavigate();
  const loc = useLocation();
  const groepen = useNav();
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const lijstRef = useRef<HTMLDivElement>(null);
  const res = useMemo<Resultaat[]>(() => {
    const z = q.trim().toLowerCase();
    const zc = z.replace(/\s/g, '');
    const past = (...xs: (string | null | undefined)[]) => !z || xs.some((x) => x?.toLowerCase().includes(z));
    const schermen: Resultaat[] = groepen.flatMap((g) => g.items).filter((i) => past(i.label)).map((i) => ({ sleutel: `s${i.naar}`, soort: 'Scherm', titel: i.label, icoon: i.icoon, naar: i.naar }));
    const acties: Resultaat[] = [
      ...(mag(s.rol, 'taken.aanmaken') ? [{ sleutel: 'a-taak', soort: 'Actie', titel: 'Nieuwe taak', icoon: 'list-checks' as IcoonNaam, naar: '/taken/nieuw' }] : []),
      ...(mag(s.rol, 'projecten.schrijven') ? [{ sleutel: 'a-proj', soort: 'Actie', titel: 'Nieuw project', icoon: 'folder-kanban' as IcoonNaam, naar: '/projecten?nieuw=1' }] : []),
    ].filter((a) => past(a.titel));
    const projecten: Resultaat[] = d.projecten
      .filter((p) => past(p.nummer, p.naam, p.klant, p.pmNaam, ...tekstVelden(p.velden)))
      .sort((a, b) => (a.status === 'AFGEROND' ? 1 : 0) - (b.status === 'AFGEROND' ? 1 : 0))
      .slice(0, z ? 8 : 5)
      .map((p) => ({ sleutel: `p${p.id}`, soort: 'Project', titel: `${p.nummer} ${p.naam}`, sub: [p.klant, p.pmNaam].filter(Boolean).join(' · '), icoon: 'folder-kanban', naar: `/projecten/${p.id}` }));
    const taken: Resultaat[] = z
      ? d.taken
          .filter((t) => past(t.titel, t.projectnummer, t.klant, ...tekstVelden(t.velden)) || (!!zc && (t.changeNr ?? '').toLowerCase().replace(/\s/g, '').includes(zc)))
          .slice(0, 8)
          .map((t) => ({ sleutel: `t${t.id}`, soort: 'Taak', titel: t.titel, sub: [t.changeNr, t.projectnummer, t.startdatum ? fmtDag(t.startdatum, 'kort') : 'niet gepland', d.engineers.find((e) => e.id === t.engineerId)?.naam].filter(Boolean).join(' · '), icoon: 'list-checks', naar: `/taken/${t.id}` }))
      : [];
    return [...acties, ...schermen.slice(0, z ? 6 : 4), ...projecten, ...(mag(s.rol, 'taken.aanmaken') ? taken : [])];
  }, [q, d, groepen, s.rol]);
  useEffect(() => setSel(0), [q]);
  useEffect(() => {
    lijstRef.current?.querySelector('.actief')?.scrollIntoView({ block: 'nearest' });
  }, [sel]);
  const kies = (r?: Resultaat) => {
    if (!r) return;
    onSluit();
    nav(r.naar, r.naar.startsWith('/taken/') ? { state: { achtergrond: (loc.state as { achtergrond?: unknown } | null)?.achtergrond ?? loc } } : undefined);
  };
  return (
    <div className="zoek-achter" onMouseDown={onSluit}>
      <div className="zoek-palet" onMouseDown={(e) => e.stopPropagation()}>
        <div className="zoek-invoer">
          <Icoon naam="search" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Zoek een project, taak, change-nummer of scherm…"
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') { e.preventDefault(); setSel((x) => Math.min(res.length - 1, x + 1)); }
              if (e.key === 'ArrowUp') { e.preventDefault(); setSel((x) => Math.max(0, x - 1)); }
              if (e.key === 'Enter') kies(res[sel]);
              if (e.key === 'Escape') onSluit();
            }}
          />
          <kbd>Esc</kbd>
        </div>
        <div className="zoek-lijst" ref={lijstRef}>
          {res.map((r, i) => (
            <button key={r.sleutel} className={i === sel ? 'actief' : ''} onMouseEnter={() => setSel(i)} onClick={() => kies(r)}>
              <Icoon naam={r.icoon} maat={16} />
              <span className="groei">
                <b>{r.titel}</b>
                {r.sub && <small>{r.sub}</small>}
              </span>
              <em>{r.soort}</em>
            </button>
          ))}
          {!res.length && <div className="leeg">Niets gevonden voor “{q}”.</div>}
        </div>
        <div className="zoek-voet"><span><kbd>↑</kbd><kbd>↓</kbd> kiezen</span><span><kbd>Enter</kbd> openen</span></div>
      </div>
    </div>
  );
}

/** SPEC §10 – Over bsmartr: versie en wijzigingen. */
export function OverBsmartr({ onSluit }: { onSluit: () => void }) {
  const [alles, setAlles] = useState(false);
  return (
    <Modal titel="Over bsmartr" onSluit={onSluit} voet={<button className="knop primair" onClick={onSluit}>Sluiten</button>}>
      <div className="kolom" style={{ gap: 14 }}>
        <div className="rij" style={{ gap: 14 }}>
          <img src="/icon-192.png" width={56} height={56} alt="" style={{ borderRadius: 12 }} />
          <div>
            <img src="/logo.svg" alt="bsmartr" style={{ height: 26, display: 'block' }} />
            <div className="mute" style={{ marginTop: 4 }}>Versie <b style={{ color: 'var(--accent-tekst)' }}>{VERSIE}</b> · {fmtDag(VERSIE_DATUM, 'jaar')}</div>
          </div>
        </div>
        <p className="klein mute" style={{ margin: 0 }}>Projecten, werkpakketten en engineers slim plannen, klassiek of agile, met je eigen agenda.</p>
        <h3>{alles ? 'Alle wijzigingen' : 'Nieuw in deze versie'}</h3>
        <WijzigingenLijst wijzigingen={alles ? WIJZIGINGEN : WIJZIGINGEN.slice(0, 1)} huidig={VERSIE} />
        <button className="knop klein" style={{ alignSelf: 'flex-start' }} onClick={() => setAlles(!alles)}>{alles ? 'Alleen de nieuwste versie' : `Alle wijzigingen (${WIJZIGINGEN.length} versies)`}</button>
        <div className="klein mute">© {new Date().getFullYear()} bsmartr</div>
      </div>
    </Modal>
  );
}

/** Uitklapmenu "⋯" voor minder gebruikte knoppen in een paginakop. */
export function MeerMenu({ children }: { children: (sluit: () => void) => ReactNode }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const k = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', k);
    return () => document.removeEventListener('mousedown', k);
  }, []);
  return (
    <div className="meer-menu" ref={ref}>
      <button className="knop" onClick={() => setOpen(!open)} title="Meer">
        <Icoon naam="ellipsis" />
      </button>
      {open && <div className="menu-lijst">{children(() => setOpen(false))}</div>}
    </div>
  );
}

/** Zijpaneel dat van rechts inschuift. */
export function Zijpaneel({ titel, onSluit, children, voet }: { titel: ReactNode; onSluit: () => void; children: ReactNode; voet?: ReactNode }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === 'Escape' && onSluit();
    window.addEventListener('keydown', k);
    return () => window.removeEventListener('keydown', k);
  }, [onSluit]);
  return (
    <div className="zijpaneel-achter" onMouseDown={onSluit}>
      <aside className="zijpaneel" onMouseDown={(e) => e.stopPropagation()}>
        <div className="zijpaneel-kop">
          <div className="groei">{titel}</div>
          <button className="icoon-knop" onClick={onSluit} title="Sluiten"><Icoon naam="x" /></button>
        </div>
        <div className="zijpaneel-inhoud">{children}</div>
        {voet && <div className="zijpaneel-voet">{voet}</div>}
      </aside>
    </div>
  );
}
